import { useMemo } from 'react';
import { FillLayer, Layer, LineLayer, MapRef, Source } from 'react-map-gl';
import { useAppSelector } from '../../../../../app/hooks';
import { Block } from '../../../../../services/entities/impl/blfApi/schemas/block';
import { ensureLayerExists } from '../../../../map/mapUtils';
import { convertBlocksToFeatureCollection } from '../../../../map/utils/convertBlocksToFeatureCollection';
import {
  MapOnboardLayerEnum,
  selectUploadedBlockGeoJSON,
} from '../../../onboardingSlice';
import { OnboardLayerExistenceDict } from '../hooks/useCheckLayersOnboarding';
import useLayerOrderOnboarding from '../hooks/useLayerOrderOnboarding';

interface Props {
  mapRef: MapRef | null;
  layerExistenceDict: OnboardLayerExistenceDict;
  blocks: Block[] | undefined;
}

export const onboardBorderLineWidth = 4;
const existingBlockColor = '#55d0ff';
const uploadedBlockColor = '#ff0000';

const BlockOutlineOnboardingLayer: React.FC<Props> = ({
  mapRef,
  layerExistenceDict,
  blocks,
}) => {
  const uploadedBlockGeoJSON = useAppSelector(selectUploadedBlockGeoJSON);
  const layerBeforeIdOutlines = useLayerOrderOnboarding(
    MapOnboardLayerEnum.blockOutlines,
    layerExistenceDict
  );
  const layerBeforeIdFileUpload = useLayerOrderOnboarding(
    MapOnboardLayerEnum.uploadedFileOutline,
    layerExistenceDict
  );

  const outlineLayerStyle = useMemo<LineLayer>(
    () => ({
      id: `${MapOnboardLayerEnum.blockOutlines}-line`,
      type: 'line',
      paint: {
        'line-color': existingBlockColor,
        'line-opacity': 1,
        'line-width': onboardBorderLineWidth,
      },
    }),
    []
  );

  const fillLayerStyle = useMemo<FillLayer>(
    () => ({
      id: `${MapOnboardLayerEnum.blockOutlines}`,
      type: 'fill',
      paint: {
        'fill-color': existingBlockColor,
        'fill-opacity': 0.4,
      },
    }),
    []
  );

  const uploadedOutlineLayerStyle = useMemo<LineLayer>(
    () => ({
      id: `${MapOnboardLayerEnum.uploadedFileOutline}-line`,
      type: 'line',
      paint: {
        'line-color': uploadedBlockColor,
        'line-opacity': 1,
        'line-width': onboardBorderLineWidth,
      },
    }),
    []
  );

  const uploadedFillLayerStyle = useMemo<FillLayer>(
    () => ({
      id: `${MapOnboardLayerEnum.uploadedFileOutline}`,
      type: 'fill',
      paint: {
        'fill-color': uploadedBlockColor,
        'fill-opacity': 0.4,
      },
    }),
    []
  );

  return (
    <>
      <Source
        key={MapOnboardLayerEnum.blockOutlines}
        id={MapOnboardLayerEnum.blockOutlines}
        type='geojson'
        data={convertBlocksToFeatureCollection(blocks || [])}
      >
        {fillLayerStyle && (
          <Layer
            key={`${MapOnboardLayerEnum.blockOutlines}-line`}
            {...fillLayerStyle}
            beforeId={ensureLayerExists(mapRef, layerBeforeIdOutlines)}
          />
        )}
        {outlineLayerStyle && (
          <Layer
            key={MapOnboardLayerEnum.blockOutlines}
            {...outlineLayerStyle}
            beforeId={ensureLayerExists(mapRef, layerBeforeIdOutlines)}
          />
        )}
      </Source>
      {uploadedBlockGeoJSON && (
        <Source
          key={`uploaded-${MapOnboardLayerEnum.blockOutlines}`}
          id={`uploaded-${MapOnboardLayerEnum.blockOutlines}`}
          type='geojson'
          data={uploadedBlockGeoJSON}
        >
          {uploadedOutlineLayerStyle && (
            <Layer
              key={`${MapOnboardLayerEnum.uploadedFileOutline}-line`}
              {...uploadedOutlineLayerStyle}
              beforeId={ensureLayerExists(mapRef, layerBeforeIdFileUpload)}
            />
          )}
          {uploadedFillLayerStyle && (
            <Layer
              key={`${MapOnboardLayerEnum.uploadedFileOutline}`}
              {...uploadedFillLayerStyle}
              beforeId={ensureLayerExists(mapRef, layerBeforeIdFileUpload)}
            />
          )}
        </Source>
      )}
    </>
  );
};

export default BlockOutlineOnboardingLayer;
